<!--

  The student's funding picture, rendered on the Budget page.
  `budget` mode: the allocation and the per-benefit breakdown.
  `expenses` mode: how each benefit is paid and the recurring payments an
  administrator settles on the student's behalf.
-->
@if (loading()) {
  <div class="empty-state"><i class="fas fa-spinner fa-spin"></i> Loading your budget…</div>
}

@if (error()) {
  <div class="alert alert-warning">
    <i class="fas fa-exclamation-circle"></i> {{ error() }}
    <button class="inline-link" (click)="load()">Try again</button>
  </div>
}

@if (!loading() && !error() && overview(); as data) {
  @if (!data.scholarshipAwardId) {
    <div class="card">
      <div class="card-body">
        <div class="empty-state">
          <i class="fas fa-chart-pie"></i>
          <h3>No scholarship budget yet</h3>
          <p>Your benefit categories and budget will appear here once a scholarship is awarded.</p>
        </div>
      </div>
    </div>
  }

  <!-- ============ Budget tab ============ -->
  @if (data.scholarshipAwardId && mode === 'budget') {
    <div class="funding-view">
      <div class="funding-header">
        <div>
          <h2 class="section-heading">Budget</h2>
          <p class="funding-subtitle">Your allocation and how it breaks down by benefit.</p>
        </div>
        <div class="card-actions">
          <a class="btn btn-primary" routerLink="/student" [queryParams]="{ section: 'budget', tab: 'claims' }">
            <i class="fas fa-file-invoice"></i> Submit a claim
          </a>
          <a class="btn btn-secondary" routerLink="/student" [queryParams]="{ section: 'budget', tab: 'expenses' }">
            <i class="fas fa-receipt"></i> See how it is paid
          </a>
        </div>
      </div>

      <div class="card">
        <div class="card-header">Allocation</div>
        <div class="card-body">
          <p class="budget-subtitle">
            <strong>{{ data.scholarshipTitle || 'Scholarship' }}</strong>
            <small>Award {{ data.awardReferenceNumber }}</small>
          </p>

          <div class="budget-summary">
            <div class="budget-figure">
              <span class="budget-label">Allocated</span>
              <span class="budget-value">{{ money(data.totalAllocation, data.currency) }}</span>
            </div>
            <div class="budget-figure">
              <span class="budget-label">Used</span>
              <span class="budget-value">{{ money(data.usedAmount, data.currency) }}</span>
            </div>
            <div class="budget-figure">
              <span class="budget-label">Remaining</span>
              <span class="budget-value">{{ money(data.remainingAmount, data.currency) }}</span>
            </div>
          </div>

          <div class="progress-track">
            <div class="progress-fill" [style.width]="progressWidth(data.budgetProgressPercent)"></div>
          </div>
          <p class="progress-caption">{{ data.budgetProgressPercent }}% of your allocation has been used.</p>
        </div>
      </div>

      <div class="card">
        <div class="card-header">Benefit breakdown</div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Benefit</th>
                  <th>Per period</th>
                  <th>Allocated</th>
                  <th>Used</th>
                  <th>Remaining</th>
                </tr>
              </thead>
              <tbody>
                @if (categories.length === 0) {
                  <tr><td colspan="5" class="empty-cell">No benefits are configured on your award.</td></tr>
                }
                @for (category of categories; track category.scholarshipAwardBenefitId) {
                  <tr>
                    <td>
                      <strong>{{ category.name }}</strong>
                      @if (category.isRecurring) { <small>Recurring</small> }
                    </td>
                    <td>
                      @if (category.amountPerPeriod) {
                        {{ money(category.amountPerPeriod, data.currency) }}
                        @if (category.allocationFrequencyName) { / {{ category.allocationFrequencyName }} }
                      } @else {
                        —
                      }
                    </td>
                    <td>{{ money(category.allocatedAmount, data.currency) }}</td>
                    <td>{{ money(category.usedAmount, data.currency) }}</td>
                    <td>{{ money(category.remainingAmount, data.currency) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ============ Expenses tab ============ -->
  @if (data.scholarshipAwardId && mode === 'expenses') {
    <div class="funding-view">
      <div class="funding-header">
        <div>
          <h2 class="section-heading">Expenses</h2>
          <p class="funding-subtitle">How each benefit is paid and the payments settled for you.</p>
        </div>
      </div>

      <div class="card">
        <div class="card-header">How your expenses are paid</div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Benefit</th>
                  <th>How it is paid</th>
                  <th>Provider</th>
                  <th>Per period</th>
                </tr>
              </thead>
              <tbody>
                @if (categories.length === 0) {
                  <tr><td colspan="4" class="empty-cell">No expense categories are configured on your award.</td></tr>
                }
                @for (category of categories; track category.scholarshipAwardBenefitId) {
                  <tr>
                    <td><strong>{{ category.name }}</strong></td>
                    <td>
                      @if (category.payDirectToProvider) {
                        <span class="status status-booked">Paid to provider</span>
                      } @else if (category.canClaim) {
                        <span class="status status-approved">You claim it</span>
                      } @else {
                        <span class="status status-cancelled">Fully used</span>
                      }
                    </td>
                    <td>{{ category.providerName || '—' }}</td>
                    <td>
                      @if (category.amountPerPeriod) {
                        {{ money(category.amountPerPeriod, data.currency) }}
                        @if (category.allocationFrequencyName) { / {{ category.allocationFrequencyName }} }
                      } @else {
                        —
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <p class="card-note">
            Benefits paid to a provider are settled by the scholarship administrator — you do not
            claim them. Everything else is claimed by you from the Claims tab.
          </p>
        </div>
      </div>

      <div class="card">
        <div class="card-header">Recurring payments</div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Benefit</th>
                  <th>Payee</th>
                  <th>Due</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                @if (data.scheduledPayments.length === 0) {
                  <tr><td colspan="6" class="empty-cell">No scheduled payments on this award.</td></tr>
                }
                @for (payment of data.scheduledPayments; track payment.id) {
                  <tr>
                    <td>{{ payment.referenceNumber }}</td>
                    <td>{{ payment.benefitName }}</td>
                    <td>{{ payment.payeeName || '—' }}</td>
                    <td>{{ payment.dueDate | date: 'mediumDate' }}</td>
                    <td>{{ money(payment.paidAmount ?? payment.amount, payment.currency) }}</td>
                    <td><span [class]="statusClass(payment.status)">{{ payment.status }}</span></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          @if (outstandingPayments.length > 0) {
            <p class="card-note">
              {{ outstandingPayments.length }} payment{{ outstandingPayments.length === 1 ? '' : 's' }}
              still to be settled by the scholarship administrator.
            </p>
          }
        </div>
      </div>
    </div>
  }
}
